import { useEffect, useState } from "react";
import { Lock, Repeat } from "lucide-react";
import { toast } from "sonner";
import { nomeDoMes, provisaoDoMes, type FolhaDoMesInput } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { reais, textoParaCentavos } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { campo, moldura, rotulo } from "../estilos";

type Campo = "salariosProducaoCents" | "salariosAdministrativoCents" | "proLaboreCents" | "inssCents" | "fgtsCents" | "decimoTerceiroEFeriasCents";
const CAMPOS: { campo: Campo; chave: string; titulo: string; dica?: string }[] = [
  { campo: "salariosProducaoCents", chave: "producao", titulo: "Salários da produção", dica: "Bruto, de quem faz os trabalhos" },
  { campo: "salariosAdministrativoCents", chave: "administrativo", titulo: "Salários do administrativo", dica: "Bruto, recepção, financeiro, entregas" },
  { campo: "proLaboreCents", chave: "proLabore", titulo: "Pró-labore" },
  { campo: "inssCents", chave: "inss", titulo: "Guia do INSS (DCTFWeb)" },
  { campo: "fgtsCents", chave: "fgts", titulo: "Guia do FGTS (FGTS Digital)" },
  { campo: "decimoTerceiroEFeriasCents", chave: "decimoEFerias", titulo: "13º e férias pagos no mês" },
];
type Rascunho = Record<Campo, string> & { vencimentoDosSalarios: string; vencimentoDasGuias: string };

const paraRascunho = (f: FolhaDoMesInput | null, venc: { vencimentoDosSalarios: string; vencimentoDasGuias: string }): Rascunho => ({
  ...(Object.fromEntries(CAMPOS.map((c) => [c.campo, f && f[c.campo] ? reais(f[c.campo]) : ""])) as Record<Campo, string>),
  vencimentoDosSalarios: f?.vencimentoDosSalarios ?? venc.vencimentoDosSalarios,
  vencimentoDasGuias: f?.vencimentoDasGuias ?? venc.vencimentoDasGuias,
});

/** A folha do mês digitada do resumo do contador: vira as contas de Pessoal e a base da provisão. */
export function FolhaDoMes({ competencia }: { competencia: string }) {
  const utils = trpc.useUtils();
  const pode = usePermissao("finance.manage");
  const dados = trpc.financeiro.folha.doMes.useQuery({ competencia }).data;
  const config = trpc.financeiro.configFinanceira.ler.useQuery().data;
  const [r, setR] = useState<Rascunho | null>(null);
  useEffect(() => {
    if (dados) setR(paraRascunho(dados.folha, dados.sugeridos));
  }, [dados]);
  const salvar = trpc.financeiro.folha.salvar.useMutation({
    onSuccess: () => {
      toast.success("Folha salva. As contas estão em Contas a pagar.");
      utils.financeiro.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  if (!dados || !r) return <div className="h-64 animate-pulse rounded-xl bg-zinc-100 dark:bg-muted" />;

  const valor = (c: Campo) => textoParaCentavos(r[c]) || 0;
  const salarios = valor("salariosProducaoCents") + valor("salariosAdministrativoCents");
  const total = CAMPOS.reduce((t, c) => t + valor(c.campo), 0);
  const enviar = () =>
    salvar.mutate({
      competencia,
      ...(Object.fromEntries(CAMPOS.map((c) => [c.campo, valor(c.campo)])) as Record<Campo, number>),
      vencimentoDosSalarios: r.vencimentoDosSalarios || null,
      vencimentoDasGuias: r.vencimentoDasGuias || null,
    });

  return (
    <section className={cn(moldura, "space-y-4 p-4")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Folha de {nomeDoMes(competencia)}</h3>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">Copie os totais do resumo da folha que o contador manda. Cada valor vira uma conta a pagar em Pessoal.</p>
        </div>
        {pode && !dados.folha && dados.anterior && (
          <button type="button" onClick={() => setR(paraRascunho(dados.anterior, dados.sugeridos))} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:underline dark:text-primary">
            <Repeat className="h-4 w-4" /> Repetir a do mês passado
          </button>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {CAMPOS.map((c) => {
          const pago = dados.pagos.includes(c.chave);
          return (
            <label key={c.campo} className="block">
              <span className={cn(rotulo, "flex items-center gap-1")}>
                {c.titulo}
                {pago && <Lock className="h-3 w-3" aria-label="Já pago" />}
              </span>
              <input value={r[c.campo]} disabled={!pode || pago} inputMode="decimal" placeholder="0,00" onChange={(e) => setR({ ...r, [c.campo]: e.target.value })} className={campo} />
              {(pago || c.dica) && <span className="mt-1 block text-[11px] text-zinc-500 dark:text-muted-foreground">{pago ? "Já pago: para mudar, estorne em Lançamentos." : c.dica}</span>}
            </label>
          );
        })}
        <label className="block">
          <span className={rotulo}>Vencimento dos salários</span>
          <input type="date" value={r.vencimentoDosSalarios} disabled={!pode} onChange={(e) => setR({ ...r, vencimentoDosSalarios: e.target.value })} className={campo} />
        </label>
        <label className="block">
          <span className={rotulo}>Vencimento das guias</span>
          <input type="date" value={r.vencimentoDasGuias} disabled={!pode} onChange={(e) => setR({ ...r, vencimentoDasGuias: e.target.value })} className={campo} />
        </label>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 pt-3 dark:border-border">
        <p className="text-sm text-zinc-600 dark:text-muted-foreground">
          Total da folha: <strong className="tabular-nums text-zinc-900 dark:text-foreground">{reais(total)}</strong>
          {config?.provisionarFolha && (
            <>
              {" · "}provisão de 13º e férias: <strong className="tabular-nums text-zinc-900 dark:text-foreground">{reais(provisaoDoMes(salarios, config.taxaDeProvisaoBps))}</strong>
            </>
          )}
        </p>
        {pode && (
          <button type="button" disabled={salvar.isPending} onClick={enviar} className="h-9 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50">
            {dados.folha ? "Salvar alterações" : "Lançar a folha"}
          </button>
        )}
      </div>
    </section>
  );
}
